import { useMemo } from 'react';

import { validateTestamentaryEntitySuggestion } from '@/modules/aiSuggestionReviewCard/AISuggestionReviewCard.utils';
import { AISuggestionReviewColumnData } from '@/modules/aiSuggestionReviewCard/components/AISuggestionReviewCell';
import { AISuggestionReviewRow } from '@/modules/aiSuggestionReviewCard/components/AISuggestionReviewRow';
import { MarkSuggestionAsDuplicateAction } from '@/modules/aiSuggestionReviewCard/components/MarkSuggestionAsDuplicateAction';
import { useAISuggestionReviewCardContext } from '@/modules/aiSuggestionReviewCard/context/aiSuggestionReviewCard.context';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import {
  testamentaryEntityAssetLocationToDisplayName,
  testamentaryEntityKindToDisplayName,
} from '@/modules/entities/testamentaryEntities/testamentaryEntities.utils';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { EntityInEstateStatus } from '@/types/schema';
import { sentenceCaseWithAcronyms } from '@/utils/textFormatting';

interface AISuggestionReviewTestamentaryEntityRowProps {
  suggestion: AiSuggestionForReviewCard;
}

/**
 * A row that displays a testamentary entity suggestion for review.
 *
 * Normalizes the suggestion data into the following columns:
 * - Display name
 * - Entity type
 * - Asset location
 */
export function AISuggestionReviewTestamentaryEntityRow({
  suggestion,
}: AISuggestionReviewTestamentaryEntityRowProps) {
  const { isTwoClientHousehold } = useHouseholdDetailsContext();

  const {
    onReviewClick,
    onDeleteClick,
    onCreateClick,
    onSuggestionMarkedDuplicate,
  } = useAISuggestionReviewCardContext();

  const { columnsData, hasWarning } = useMemo<{
    columnsData: AISuggestionReviewColumnData[];
    hasWarning: boolean;
  }>(() => {
    const suggestionData = suggestion.onboardingTestamentaryEntity;
    if (!suggestionData) return { columnsData: [], hasWarning: false };

    const { assetLocationMissing, hasWarning } =
      validateTestamentaryEntitySuggestion(suggestion, isTwoClientHousehold);

    const columnsData: AISuggestionReviewColumnData[] = [
      {
        label: 'Display name',
        values: [
          {
            displayText:
              suggestionData.displayName ??
              sentenceCaseWithAcronyms(
                suggestionData.testamentaryEntityName || ''
              ),
            showWarning: false,
            notes: suggestionData.notes,
          },
        ],
      },
      {
        label: 'Entity type',
        values: [
          {
            displayText: suggestionData.testamentaryEntityKind
              ? testamentaryEntityKindToDisplayName(
                  suggestionData.testamentaryEntityKind
                )
              : '-',
            showWarning: false,
          },
        ],
      },
      {
        label: 'Asset location',
        values: [
          {
            displayText: suggestionData?.inEstateStatus
              ? testamentaryEntityAssetLocationToDisplayName(
                  isTwoClientHousehold
                    ? suggestionData?.inEstateStatus || ''
                    : EntityInEstateStatus.OutOfEstate // single-client households already create TE's as out-of-estate
                )
              : '-',
            showWarning: assetLocationMissing,
            warningTooltip: assetLocationMissing
              ? 'Missing asset location. Review required.'
              : undefined,
          },
        ],
      },
    ];

    return { columnsData, hasWarning };
  }, [isTwoClientHousehold, suggestion]);

  if (!suggestion.onboardingTestamentaryEntity) {
    return null;
  }

  const parentTrustName =
    suggestion.onboardingTestamentaryParentKgV2Node?.namePrimary;

  return (
    <AISuggestionReviewRow
      suggestionID={suggestion.id}
      rowHasWarning={hasWarning}
      columnsData={columnsData}
      subtitle={parentTrustName ? `From ${parentTrustName}` : undefined}
      onCreateClick={onCreateClick}
      onReviewClick={onReviewClick}
      onDeleteClick={onDeleteClick}
      renderAdditionalAction={({ disabled }) => (
        <MarkSuggestionAsDuplicateAction
          suggestion={suggestion}
          disabled={disabled}
          onMarkedDuplicate={onSuggestionMarkedDuplicate}
        />
      )}
    />
  );
}
